import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { TimesCircleAltIcon, AngleDownIcon } from '../../icons/react';

/* eslint-disable react/require-default-props */
interface RCChipProps {
  /** RCChips are simpler than RCButtons and expect only a toggled state. */
  selected?: boolean;

  /** Appends a numeric indicator to the chip. Positive count values only. */
  count?: number;

  /**
   * Optionally adds an icon to indicate that this button opens a menu or popover.
   * Note: This prop is automatically applied when used with RCPopover.
  */
  'aria-haspopup'?: boolean | 'dialog' | 'menu';

  /** Standard change handler; this component doesn't track its own state. */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;

  /** We allow any markup here for overall consistency but this should likely remain a simple string */
  children: React.ReactNode;

  /**
   * These sizes are set up to replicate our RCButton sizes.
   * `mini` RCChips don't render a close icon and are intended for use in a group of selectable items, e.g. suggestions below an input field.
  */
  size?: 'small' | 'mini';
}

/**
 * A special simplified button control for use when the user is taking action _within_ the context of the page they're on.
 *
 * They're often used in groups; in those cases, put them inside the `RCChip.Collection` wrapper component
 * to standardize spacing & wrapping.
 */
const RCChip = React.forwardRef<HTMLButtonElement, RCChipProps>(
  function RCChip({
    selected = undefined,
    count = undefined,
    'aria-haspopup': ariaHasPopup = undefined,
    onClick = undefined,
    size = 'small',
    children,
    ...passthroughProps
  }: RCChipProps, propRef) {
    const classes = classNames(
      'rc-chip',
      {
        'rc-chip--small': size === 'small',
        'rc-chip--mini': size === 'mini',
      },
    );

    function renderCount() {
      if (!count) return null;
      if (count <= 0) return null;

      return (
        <>
          <span aria-hidden>
            {` (${count})`}
          </span>
          <span className="visually-hidden">
            {I18n.t('cadence.RCChip.applied', { count: count })}
          </span>
        </>
      );
    }

    function renderIcon() {
      if (ariaHasPopup) {
        return (
          <div className="rc-chip__caret">
            <AngleDownIcon />
          </div>
        );
      }
      if (selected) {
        return (
          <div className="rc-chip__remove">
            <TimesCircleAltIcon />
          </div>
        );
      }
      return null;
    }

    return (
      <button
        className={classes}
        type="button"
        aria-pressed={selected}
        aria-haspopup={ariaHasPopup}
        onClick={onClick}
        ref={propRef}
        {...passthroughProps}
      >
        <span>
          {children}
          {renderCount()}
        </span>
        {renderIcon()}
      </button>
    );
  },
);

// Wrapper container to standardize spacing & wrapping.

interface RCChipCollectionProps {
  nowrap?: 'tablet',
  children,
}

function RCChipCollection({
  nowrap = undefined,
  children,
}: RCChipCollectionProps) {
  const classes = classNames(
    'd-flex fx-wrap gap-2 pr-10',
    {
      'tablet-d-block tablet-ws-nowrap': nowrap === 'tablet',
    },
  );

  if (nowrap === 'tablet') {
    return (
      <div className={classes}>
        {
          React.Children.map(children, (child, idx) => {
            return (
              <div
                key={idx}
                className="tablet-d-inline-block tablet-mr-2"
              >
                {child}
              </div>
            );
          })
        }
      </div>
    );
  }

  return (
    <div className={classes}>
      {children}
    </div>
  );
}

// Dot notation assignments and component export

const RCChipComponents = Object.assign(
  RCChip, {
    Collection: RCChipCollection,
  },
);

export { RCChipComponents as RCChip };
